import { useEffect, useState, useCallback, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';
import { useAlert } from 'react-alert';
import { Checkout } from '../types/checkout';
import { Api } from './useApi';
import {
  MoedaVendaDescricao,
  MoedaVendaEnum,
} from '../types/enums/moeda-venda.enum';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import { CreateCreditCardPuchaseContract } from '../types/create-credit-card-puchase-contract';
import { Sell } from '../types/venda';
import { IResponsePadrao } from '../types/response-padrao';
import { getConfig } from '../utils/get-config';

type Params = {
  checkout: Checkout;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  onSuccess: (res: IResponsePadrao<Sell>) => void;
  supportsApplePay: boolean;
};

type Result = {
  applePayAvailable: boolean;
  handleApplePayClick: () => void;
  isOpeningApplePay: boolean;
};

export const useApplePayExpress = ({
  checkout,
  api,
  gerarTokenRecaptcha,
  onSuccess,
  supportsApplePay,
}: Params): Result => {
  const stripe = useStripe();
  const alert = useAlert();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [isOpeningApplePay, setIsOpeningApplePay] = useState(false);

  // Refs para evitar loops infinitos (funções que não devem estar nas dependências)
  const apiRef = useRef(api);
  const gerarTokenRecaptchaRef = useRef(gerarTokenRecaptcha);
  const onSuccessRef = useRef(onSuccess);
  const alertRef = useRef(alert);
  const checkoutRef = useRef(checkout);

  // Atualiza refs quando mudam
  useEffect(() => {
    apiRef.current = api;
    gerarTokenRecaptchaRef.current = gerarTokenRecaptcha;
    onSuccessRef.current = onSuccess;
    alertRef.current = alert;
    checkoutRef.current = checkout;
  }, [api, gerarTokenRecaptcha, onSuccess, alert, checkout]);

  // Ref para evitar recriar paymentRequest desnecessariamente
  const lastCheckoutIdRef = useRef<string | undefined>(undefined);
  const lastSubtotalRef = useRef<number | undefined>(undefined);
  const paymentRequestRef = useRef<PaymentRequest | null>(null);

  useEffect(() => {
    console.log('[APPLEPAY][EFFECT] ========== INÍCIO ==========');
    console.log('[APPLEPAY][EFFECT] init', {
      supportsApplePay,
      hasStripe: !!stripe,
      checkoutId: checkout?.id,
      subtotal: checkout?.subtotal,
      moeda: checkout?.moeda,
      pais: checkout?.pais,
      produtoNome: checkout?.produto?.nome,
      lastCheckoutId: lastCheckoutIdRef.current,
      lastSubtotal: lastSubtotalRef.current,
      hasPaymentRequest: !!paymentRequestRef.current,
    });

    if (!supportsApplePay) {
      console.log('[APPLEPAY][EFFECT] ❌ supportsApplePay=false → abort');
      return;
    }
    console.log('[APPLEPAY][EFFECT] ✅ supportsApplePay=true');

    if (!stripe) {
      console.log('[APPLEPAY][EFFECT] ❌ stripe ainda não carregado → abort');
      return;
    }
    console.log('[APPLEPAY][EFFECT] ✅ stripe carregado');

    const checkoutId = checkout?.id;
    const subtotal = checkout?.subtotal;

    console.log(
      '[APPLEPAY][EFFECT] Verificando se precisa recriar paymentRequest',
      {
        checkoutId,
        subtotal,
        lastCheckoutId: lastCheckoutIdRef.current,
        lastSubtotal: lastSubtotalRef.current,
        hasPaymentRequest: !!paymentRequestRef.current,
      },
    );

    // Evita recriar paymentRequest se os dados não mudaram
    if (
      lastCheckoutIdRef.current === checkoutId &&
      lastSubtotalRef.current === subtotal &&
      paymentRequestRef.current !== null
    ) {
      console.log(
        '[APPLEPAY][EFFECT] ⏭️ Dados do checkout não mudaram, mantendo paymentRequest existente',
      );
      return;
    }

    if (!checkoutId || !subtotal) {
      console.log(
        '[APPLEPAY][EFFECT] ❌ checkout incompleto (id/subtotal) → abort',
        { id: checkoutId, subtotal },
      );
      return;
    }
    console.log('[APPLEPAY][EFFECT] ✅ checkout completo');

    if (typeof window === 'undefined') {
      console.warn('[APPLEPAY][EFFECT] ❌ window não disponível');
      return;
    }

    if (!(window as any).PaymentRequest) {
      console.warn(
        '[APPLEPAY][EFFECT] ❌ PaymentRequest não disponível no window',
      );
      return;
    }
    console.log('[APPLEPAY][EFFECT] ✅ PaymentRequest disponível');

    const currency =
      MoedaVendaDescricao[checkout.moeda ?? MoedaVendaEnum.BRL]?.toLowerCase();
    console.log('[APPLEPAY][EFFECT] Resolvendo currency', {
      moedaEnum: checkout.moeda,
      moedaDescricao: MoedaVendaDescricao[checkout.moeda ?? MoedaVendaEnum.BRL],
      currency,
    });
    if (!currency) {
      console.warn('[APPLEPAY][EFFECT] ❌ currency inválida → abort', {
        moeda: checkout.moeda,
        moedaDescricao:
          MoedaVendaDescricao[checkout.moeda ?? MoedaVendaEnum.BRL],
      });
      return;
    }
    console.log('[APPLEPAY][EFFECT] ✅ currency resolvida:', currency);

    const country = checkout.pais || 'US';
    const amountInCents = Math.round((checkout.subtotal ?? 0) * 100);

    console.log('[APPLEPAY][EFFECT] Preparando dados para paymentRequest', {
      country,
      currency,
      amountInCents,
      subtotalOriginal: checkout.subtotal,
      label: checkout?.produto?.nome ?? 'Order',
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
    });

    let pr: PaymentRequest;
    try {
      console.log('[APPLEPAY][EFFECT] Criando stripe.paymentRequest...');
      pr = stripe.paymentRequest({
        country,
        currency,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
      });
      console.log('[APPLEPAY][EFFECT] ✅ paymentRequest criado com sucesso');
    } catch (e) {
      console.error('[APPLEPAY][EFFECT] ❌ Erro ao criar PaymentRequest', e);
      return;
    }

    // Limpa paymentRequest anterior se existir
    if (paymentRequestRef.current) {
      console.log('[APPLEPAY][EFFECT] Limpando paymentRequest anterior...');
      try {
        paymentRequestRef.current.off('paymentmethod');
        paymentRequestRef.current.off('shippingaddresschange');
        paymentRequestRef.current.off('shippingoptionchange');
        console.log(
          '[APPLEPAY][EFFECT] ✅ Listeners removidos do paymentRequest anterior',
        );
      } catch (e) {
        console.warn('[APPLEPAY][EFFECT] ⚠️ Erro ao limpar listeners:', e);
      }
    }

    const forceWallets = getConfig('REACT_APP_SHOW_WALLETS') === 'true';
    if (forceWallets) {
      console.log(
        '[APPLEPAY][EFFECT] 🧪 DEV OVERRIDE: REACT_APP_SHOW_WALLETS=true',
      );
      setApplePayAvailable(true);
      setPaymentRequest(pr);
      paymentRequestRef.current = pr;

      // Salva pra evitar recriação (igual seu fluxo normal)
      lastCheckoutIdRef.current = checkoutId;
      lastSubtotalRef.current = subtotal;

      // IMPORTANTE: não chama canMakePayment nesse modo
    } else {
      console.log('[APPLEPAY][EFFECT] Chamando pr.canMakePayment()...');
      pr.canMakePayment()
        .then((result) => {
          console.log('[APPLEPAY][EFFECT] canMakePayment result recebido:', {
            result,
            hasApplePay: !!result?.applePay,
            hasGooglePay: !!result?.googlePay,
            hasLink: !!result?.link,
            hasWallet: !!result?.wallet,
          });

          const available = !!(result && (result.applePay || result.wallet));
          console.log('[APPLEPAY][EFFECT] Apple Pay disponível?', available);

          setApplePayAvailable(available);
          if (!available) {
            console.log(
              '[APPLEPAY][EFFECT] ❌ canMakePayment=false → desabilitando Apple Pay',
              {
                reason: !result
                  ? 'result é null/undefined'
                  : !result.applePay && !result.wallet
                    ? 'nem applePay nem wallet disponíveis'
                    : 'desconhecido',
              },
            );
            setPaymentRequest(null);
            paymentRequestRef.current = null;
          } else {
            console.log(
              '[APPLEPAY][EFFECT] ✅ canMakePayment=true → Apple Pay disponível!',
            );
            setPaymentRequest(pr);
            paymentRequestRef.current = pr;
            // Salva os dados do checkout para evitar recriação
            lastCheckoutIdRef.current = checkoutId;
            lastSubtotalRef.current = subtotal;
            console.log(
              '[APPLEPAY][EFFECT] Dados salvos para evitar recriação:',
              {
                checkoutId,
                subtotal,
              },
            );
          }
        })
        .catch((err) => {
          console.error('[APPLEPAY][EFFECT] ❌ canMakePayment lançou erro:', {
            error: err,
            message: err?.message,
            stack: err?.stack,
          });
          setApplePayAvailable(false);
          setPaymentRequest(null);
          paymentRequestRef.current = null;
        });
    }

    // Handler para quando o endereço de envio é selecionado
    pr.on('shippingaddresschange', (ev) => {
      console.log('[APPLEPAY][EVENT] 📍 shippingaddresschange event recebido');
      console.log('[APPLEPAY][EVENT] shippingAddress completo:', {
        shippingAddress: ev.shippingAddress,
        city: ev.shippingAddress?.city,
        country: ev.shippingAddress?.country,
        postalCode: ev.shippingAddress?.postalCode,
        region: ev.shippingAddress?.region,
        addressLine: ev.shippingAddress?.addressLine,
      });

      const shippingOptions = [
        {
          id: 'standard',
          label: 'Entrega Padrão',
          detail: 'Entrega em 5-7 dias úteis',
          amount: 0, // Frete grátis ou valor do frete
        },
      ];

      const updateData = {
        status: 'success' as const,
        shippingOptions,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents, // Mantém o valor original
        },
      };

      console.log('[APPLEPAY][EVENT] Fornecendo shippingOptions:', {
        shippingOptions,
        total: updateData.total,
      });

      try {
        ev.updateWith(updateData);
        console.log(
          '[APPLEPAY][EVENT] ✅ shippingOptions fornecidas com sucesso',
        );
      } catch (e) {
        console.error('[APPLEPAY][EVENT] ❌ Erro ao chamar ev.updateWith:', e);
      }
    });

    // Handler para quando a opção de envio é alterada
    pr.on('shippingoptionchange', (ev) => {
      console.log('[APPLEPAY][EVENT] 🚚 shippingoptionchange event recebido');
      console.log('[APPLEPAY][EVENT] shippingOption:', {
        shippingOption: ev.shippingOption,
        id: ev.shippingOption?.id,
        label: ev.shippingOption?.label,
        amount: ev.shippingOption?.amount,
      });

      const updateData = {
        status: 'success' as const,
        total: {
          label: checkout?.produto?.nome ?? 'Order',
          amount: amountInCents, // Pode ajustar aqui se houver frete
        },
      };

      console.log('[APPLEPAY][EVENT] Atualizando total:', updateData.total);

      try {
        ev.updateWith(updateData);
        console.log('[APPLEPAY][EVENT] ✅ Total atualizado com sucesso');
      } catch (e) {
        console.error('[APPLEPAY][EVENT] ❌ Erro ao atualizar total:', e);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      console.log('[APPLEPAY][EVENT] 💳 paymentmethod event recebido');
      console.log('[APPLEPAY][EVENT] Dados do evento:', {
        paymentMethodId: ev.paymentMethod?.id,
        paymentMethodType: ev.paymentMethod?.type,
        payerEmail: ev.payerEmail,
        payerName: ev.payerName,
        payerPhone: ev.payerPhone,
        shippingAddress: ev.shippingAddress,
        shippingOption: ev.shippingOption,
        complete: typeof ev.complete,
      });

      // Usa refs para acessar as funções mais recentes
      const currentCheckout = checkoutRef.current;
      const currentApi = apiRef.current;
      const currentGerarToken = gerarTokenRecaptchaRef.current;
      const currentOnSuccess = onSuccessRef.current;
      const currentAlert = alertRef.current;

      console.log('[APPLEPAY][EVENT] Refs carregadas:', {
        hasCheckout: !!currentCheckout,
        hasApi: !!currentApi,
        hasGerarToken: !!currentGerarToken,
        hasOnSuccess: !!currentOnSuccess,
        hasAlert: !!currentAlert,
      });

      if (!currentCheckout?.id) {
        console.error(
          '[APPLEPAY][EVENT] ❌ checkout não disponível no handler',
          {
            checkout: currentCheckout,
            checkoutId: currentCheckout?.id,
          },
        );
        try {
          ev.complete('fail');
          console.log('[APPLEPAY][EVENT] ✅ ev.complete("fail") chamado');
        } catch (e) {
          console.error(
            '[APPLEPAY][EVENT] ❌ Erro ao chamar ev.complete("fail"):',
            e,
          );
        }
        return;
      }

      console.log('[APPLEPAY][EVENT] ✅ Checkout disponível:', {
        checkoutId: currentCheckout.id,
        produtoId: currentCheckout.produtoId,
      });

      try {
        console.log('[APPLEPAY][EVENT] 🔐 Iniciando geração de recaptcha...');
        const recaptchaToken = await currentGerarToken('/venda');
        console.log('[APPLEPAY][EVENT] ✅ recaptcha gerado com sucesso', {
          tokenLength: recaptchaToken?.length,
          tokenPreview: recaptchaToken?.substring(0, 20) + '...',
        });

        const afiliadoId = getUrlParams('affiliate') ?? undefined;
        const shipping = ev.shippingAddress as any;
        const firstAddressLine = shipping?.addressLine?.[0] ?? '';
        const fallbackDistrict = shipping?.addressLine?.[1];

        console.log('[APPLEPAY][EVENT] Processando dados de envio:', {
          shippingAddress: shipping,
          postalCode: shipping?.postalCode ?? shipping?.postal_code,
          region: shipping?.region,
          administrativeArea: shipping?.administrativeArea,
          city: shipping?.city,
          locality: shipping?.locality,
          addressLine: shipping?.addressLine,
        });

        const payload: CreateCreditCardPuchaseContract = {
          metodoPagamento: MetodoPagamentoVendaEnum.APPLE_PAY,
          produtoId: currentCheckout.produtoId,
          checkoutId: currentCheckout.id,
          recaptchaToken,
          afiliadoId,

          emailComprador: ev.payerEmail ?? '',
          nomeComprador: ev.payerName ?? '',
          sobrenomeComprador: ev.payerName ?? '',
          telefoneComprador: (ev.payerPhone ?? '').replace(/\D/g, ''),
          postalCode:
            shipping?.postalCode ?? shipping?.postal_code ?? undefined,
          state: shipping?.region ?? shipping?.administrativeArea ?? undefined,
          city: shipping?.city ?? shipping?.locality ?? undefined,
          district: shipping?.dependentLocality || '-',
          address: shipping?.addressLine?.[0],
          number: shipping?.addressLine?.[0]?.match(/\d+$/)?.[0] || '-',
          complement: shipping?.addressLine?.[1],
          token: ev.paymentMethod.id,
          installments: 1,
          cpfComprador: '',
          utmSource: getUrlParams('utm_source'),
          utmCampaign: getUrlParams('utm_campaign'),
          utmMedium: getUrlParams('utm_medium'),          utmContent: getUrlParams('utm_content'),
          utmTerm: getUrlParams('utm_term'),
          urlParams: getAllUrlParams(),
        };

        console.log('[APPLEPAY][EVENT] 📦 Payload montado para createCC:', {
          metodoPagamento: payload.metodoPagamento,
          produtoId: payload.produtoId,
          checkoutId: payload.checkoutId,
          emailComprador: payload.emailComprador,
          nomeComprador: payload.nomeComprador,
          telefoneComprador: payload.telefoneComprador,
          postalCode: payload.postalCode,
          state: payload.state,
          city: payload.city,
          address: payload.address,
          token: payload.token,
          hasRecaptchaToken: !!payload.recaptchaToken,
          hasAfiliadoId: !!payload.afiliadoId,
        });

        console.log('[APPLEPAY][EVENT] 🌐 Chamando API Venda.createCC...');
        const res = await currentApi.Venda.createCC(payload);
        console.log('[APPLEPAY][EVENT] 📥 Resposta createCC recebida:', {
          hasError: !!res.error,
          hasData: !!res.data,
          error: res.error,
          message: res.message,
          dataPreview: res.data
            ? {
                id: res.data.id,
                status: res.data.status,
              }
            : null,
        });

        if (res.error) {
          console.error('[APPLEPAY][EVENT] ❌ createCC retornou erro:', {
            error: res.error,
            message: res.message,
            messageArray: res.message,
            fullResponse: res,
          });

          try {
            ev.complete('fail');
            console.log(
              '[APPLEPAY][EVENT] ✅ ev.complete("fail") chamado após erro da API',
            );
          } catch (e) {
            console.error(
              '[APPLEPAY][EVENT] ❌ Erro ao chamar ev.complete("fail"):',
              e,
            );
          }

          const errorMessage =
            res.message?.[0] ??
            'Não foi possível concluir o pagamento com Apple Pay.';
          console.log('[APPLEPAY][EVENT] Mostrando alerta:', errorMessage);
          currentAlert.show(errorMessage);
          return;
        }

        console.log('[APPLEPAY][EVENT] ✅ createCC sucesso!', {
          vendaId: res.data?.id,
          status: res.data?.status,
        });

        emitUtmifyEvent({
          event: 'waiting_payment',
          vendaId: res.data?.id,
        });

        try {
          ev.complete('success');
          console.log('[APPLEPAY][EVENT] ✅ ev.complete("success") chamado');
        } catch (e) {
          console.error(
            '[APPLEPAY][EVENT] ❌ Erro ao chamar ev.complete("success"):',
            e,
          );
        }

        console.log('[APPLEPAY][EVENT] Chamando onSuccess callback...');
        currentOnSuccess(res);
        console.log('[APPLEPAY][EVENT] ✅ onSuccess callback executado');
      } catch (err: any) {
        console.error('[APPLEPAY][EVENT] ❌ Erro no handler paymentmethod:', {
          error: err,
          message: err?.message,
          stack: err?.stack,
          name: err?.name,
          code: err?.code,
          response: err?.response,
          request: err?.request,
        });

        try {
          ev.complete('fail');
          console.log(
            '[APPLEPAY][EVENT] ✅ ev.complete("fail") chamado após exceção',
          );
        } catch (e) {
          console.error(
            '[APPLEPAY][EVENT] ❌ Falha ao chamar ev.complete("fail") após erro:',
            e,
          );
        }

        const errorMessage =
          err?.message ||
          'Erro inesperado ao processar Apple Pay. Tente novamente.';
        console.log(
          '[APPLEPAY][EVENT] Mostrando alerta de erro:',
          errorMessage,
        );
        currentAlert.show(errorMessage);
      }
    });

    pr.on('cancel', () => {
      console.log('[APPLEPAY][EVENT] ❌ Pagamento cancelado pelo usuário');
      setIsOpeningApplePay(false);
    });

    // Não seta paymentRequest aqui, apenas após canMakePayment retornar true
    console.log(
      '[APPLEPAY][EFFECT] ✅ paymentRequest configurado e listeners adicionados',
    );
    console.log('[APPLEPAY][EFFECT] ========== FIM ==========');

    // Cleanup function
    return () => {
      console.log('[APPLEPAY][EFFECT] 🧹 Cleanup: removendo listeners...');
      if (pr) {
        try {
          pr.off('paymentmethod');
          pr.off('shippingaddresschange');
          pr.off('shippingoptionchange');
          pr.off('cancel');
          console.log('[APPLEPAY][EFFECT] ✅ Listeners removidos no cleanup');
        } catch (e) {
          console.warn(
            '[APPLEPAY][EFFECT] ⚠️ Erro ao remover listeners no cleanup:',
            e,
          );
        }
      }
    };
  }, [
    stripe,
    supportsApplePay,
    checkout?.id,
    checkout?.subtotal,
    checkout?.moeda,
    checkout?.pais,
    checkout?.produto?.nome,
    // Removidas dependências que causam loops:
    // api, gerarTokenRecaptcha, onSuccess, alert
    // Essas são acessadas via refs dentro do handler
  ]);

  const handleApplePayClick = useCallback(() => {
    console.log('[APPLEPAY][CLICK] 🖱️ handleApplePayClick chamado');
    console.log('[APPLEPAY][CLICK] Estado atual:', {
      hasPaymentRequest: !!paymentRequest,
      applePayAvailable,
      isOpeningApplePay,
      paymentRequestRef: !!paymentRequestRef.current,
    });

    if (!paymentRequest) {
      console.warn(
        '[APPLEPAY][CLICK] ❌ Bloqueado: sem paymentRequest configurado',
      );
      return;
    }

    if (!applePayAvailable) {
      console.warn('[APPLEPAY][CLICK] ❌ Bloqueado: applePayAvailable=false');
      return;
    }

    try {
      console.log(
        '[APPLEPAY][CLICK] ✅ Condições atendidas, abrindo Apple Pay...',
      );
      setIsOpeningApplePay(true);
      console.log('[APPLEPAY][CLICK] Chamando paymentRequest.show()...');
      const showPromise = paymentRequest.show() as unknown as
        | Promise<unknown>
        | undefined;
      showPromise
        ?.then(() => {
          console.log('[APPLEPAY][CLICK] ✅ show() resolvido');
          setIsOpeningApplePay(false);
        })
        ?.catch(() => {
          console.log('[APPLEPAY][CLICK] ⚠️ show() rejeitado (cancelado pelo usuário)');
          setIsOpeningApplePay(false);
        });
      console.log(
        '[APPLEPAY][CLICK] ✅ paymentRequest.show() chamado com sucesso',
      );
    } catch (e) {
      console.error('[APPLEPAY][CLICK] ❌ Falha ao abrir Apple Pay:', {
        error: e,
        message: (e as any)?.message,
        stack: (e as any)?.stack,
      });
      setIsOpeningApplePay(false);
    }
  }, [paymentRequest, applePayAvailable, isOpeningApplePay]);

  return { applePayAvailable, handleApplePayClick, isOpeningApplePay };
};
